'use client';

import { useCallback, useMemo, useState } from 'react';
import { Image as ImageIcon, Box, Film, Mail, Check, AlertCircle } from 'lucide-react';

import { getShareTemplateUrl, type ShareTemplateFormat } from '@/lib/share/template-urls';

// Try the native share sheet first (iOS save-to-photos works through
// this), fall back to a direct download anchor click.
async function shareOrDownload(
  blob: Blob,
  filename: string,
  mime: string,
  captionText: string,
): Promise<{ method: 'share' | 'download' }> {
  // Only reach for the native share sheet on touch devices (iOS save-to-photos,
  // Android share). On desktop, navigator.share pops an oddly-placed OS share
  // sheet for what reads as a plain "Save", so we download the file directly.
  const isTouch =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  if (isTouch && typeof navigator !== 'undefined' && 'share' in navigator && 'canShare' in navigator) {
    try {
      const file = new File([blob], filename, { type: mime });
      const data: ShareData = { files: [file], text: captionText };
      const navShare = navigator as Navigator & { canShare: (d: ShareData) => boolean };
      if (navShare.canShare(data)) {
        await navigator.share(data);
        return { method: 'share' };
      }
    } catch {
      // Fall through to download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return { method: 'download' };
}

// Compact inline share row that lives inside the cube card on the
// results page. Three icon buttons (2D grid / cube still / cube GIF)
// plus a small email button that expands a single-input form. Replaced
// the big "Take this with you" InlineShareSection block (removed 2026-07).
//
// Save logic: fetch the pre-baked template URL via cache:force-cache
// (so we hit the same edge entry as the preview), then dispatch to
// navigator.share or downloads via shareOrDownload.

type SaveState = 'idle' | 'loading' | 'success' | 'error';

type Props = {
  econ: number;
  gov: number;
  social: number;
  ideologyLabel: string;
  /** Rendered at the foot of the card, under the buttons. */
  footer?: React.ReactNode;
  /** Resolved result name shown above the save/share row (the payoff). */
  resultName?: string;
  quizType?: 'short' | 'long';
  quizResponseId?: string;
  onSaved?: (info: { format: ShareTemplateFormat; method: 'share' | 'download' }) => void;
  onEmailSubmitted?: () => void;
};

const TILES: Array<{ id: ShareTemplateFormat; label: string; Icon: typeof ImageIcon }> = [
  { id: '2d', label: '2D', Icon: ImageIcon },
  { id: '3d-still', label: 'Cube', Icon: Box },
  { id: '3d-gif', label: 'GIF', Icon: Film },
];

export default function CompactShareRow({
  econ,
  gov,
  social,
  ideologyLabel,
  footer,
  resultName,
  quizType,
  quizResponseId,
  onSaved,
  onEmailSubmitted,
}: Props) {
  const urls = useMemo(() => {
    return {
      '2d': getShareTemplateUrl({ econ, gov, social, format: '2d', quizType }),
      '3d-still': getShareTemplateUrl({ econ, gov, social, format: '3d-still', quizType }),
      '3d-gif': getShareTemplateUrl({ econ, gov, social, format: '3d-gif', quizType }),
    } satisfies Record<ShareTemplateFormat, string>;
  }, [econ, gov, social, quizType]);

  const slug = useMemo(() => {
    return (ideologyLabel || 'votely')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'votely';
  }, [ideologyLabel]);
  const captionText = `I got "${ideologyLabel}" on the Votely quiz. Take it: votelyquiz.com`;

  const [states, setStates] = useState<Record<ShareTemplateFormat, SaveState>>({
    '2d': 'idle',
    '3d-still': 'idle',
    '3d-gif': 'idle',
  });

  const handleSave = useCallback(
    async (format: ShareTemplateFormat) => {
      setStates((s) => ({ ...s, [format]: 'loading' }));
      try {
        const res = await fetch(urls[format], { cache: 'force-cache' });
        if (!res.ok) throw new Error(`fetch ${res.status}`);
        const blob = await res.blob();
        const ext = format === '3d-gif' ? 'gif' : 'png';
        const mime = format === '3d-gif' ? 'image/gif' : 'image/png';
        const filename = `votely-${slug}-${format}.${ext}`;
        const { method } = await shareOrDownload(blob, filename, mime, captionText);
        setStates((s) => ({ ...s, [format]: 'success' }));
        onSaved?.({ format, method });
        setTimeout(() => setStates((s) => ({ ...s, [format]: 'idle' })), 2000);
      } catch {
        setStates((s) => ({ ...s, [format]: 'error' }));
        setTimeout(() => setStates((s) => ({ ...s, [format]: 'idle' })), 2500);
      }
    },
    [urls, slug, captionText, onSaved],
  );

  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState<SaveState>('idle');

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailState('error');
      return;
    }
    setEmailState('loading');
    try {
      const r = await fetch('/api/share/email-artifacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          ideologyLabel,
          econ,
          gov,
          social,
          quizType,
          quizResponseId,
        }),
      });
      if (!r.ok) throw new Error('send failed');
      setEmailState('success');
      onEmailSubmitted?.();
    } catch {
      setEmailState('error');
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <div className="flex flex-wrap items-center justify-center text-center gap-x-2 gap-y-1 mb-2.5">
        {resultName && (
          <span className="text-lg font-extrabold text-foreground bg-primary/20 rounded-md px-2 py-0.5 wrap-break-word hyphens-auto">{resultName}</span>
        )}
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/45">Save or share your result</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {TILES.map(({ id, label, Icon }) => {
          const st = states[id];
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleSave(id)}
              disabled={st === 'loading'}
              aria-label={`Save ${label}`}
              className={
                'inline-flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium transition-colors border ' +
                (st === 'success'
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : st === 'error'
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-surface text-foreground border-border hover:bg-cta-secondary')
              }
            >
              {st === 'success' ? <Check size={15} /> : st === 'error' ? <AlertCircle size={15} /> : <Icon size={15} />}
              {label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setEmailOpen((v) => !v)}
          aria-expanded={emailOpen}
          aria-label="Email all three to me"
          className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-semibold bg-cta text-white border border-cta hover:bg-cta-hover transition-colors"
        >
          <Mail size={15} />
          Email
        </button>
      </div>

      {emailOpen && (
        <form onSubmit={handleEmailSubmit} className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="email"
            required
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            disabled={emailState === 'loading' || emailState === 'success'}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailState !== 'idle') setEmailState('idle');
            }}
            className="px-3 py-2.5 text-sm rounded-lg border border-border bg-surface w-full sm:w-56 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={emailState === 'loading' || emailState === 'success'}
            className={
              'inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ' +
              (emailState === 'success'
                ? 'bg-emerald-600 text-white'
                : emailState === 'error'
                  ? 'bg-amber-600 text-white'
                  : 'bg-cta text-white hover:bg-cta-hover disabled:opacity-60')
            }
          >
            {emailState === 'success' ? <Check size={16} /> : <Mail size={16} />}
            {emailState === 'loading' ? 'Sending…' : emailState === 'success' ? 'Sent' : emailState === 'error' ? 'Try again' : 'Send'}
          </button>
        </form>
      )}
      {footer}
    </div>
  );
}
